import React, { useState, useEffect, useContext } from "react";
import { AppContext } from "../context/AppContext";
import translate from "../i18n/Translator";
import Surface from "../components/Surface";
import { exportReport, getAnnouncement, getReport } from "../api/AnnouncementAPI";
import { Announcement, AnouncementReport, ExportResponse } from "../model/Announcement";
import { RouterParams } from "../router/RouterParams";
import Progress from "../components/Progress";
import DetailsIcon from '@material-ui/icons/VisibilityTwoTone';
import { formatDate } from "../components/DateFormat";
import { CardHeader, Card, CardContent, Typography, List, ListItem, ListItemText, ListItemSecondaryAction, Grid, Divider, Box, Fab } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import Gridable from "../components/Gridable";
import DateFormat from '../components/DateFormat';
import Pagination, { initialPage } from '../components/Pagination';
import { initialPageSize, getOffset } from '../components/Pagination';
import CustomBackdrop from '../components/CustomBackdrop';
import { ErrorSnackbar } from '../components/Snackbars';
import DownloadIcon from '@material-ui/icons/GetAppTwoTone';

export default function ProviderAnnouncementsReportView({ match }: RouterParams) {
    const context = useContext(AppContext);
    const [status, setStatus] = useState<string>("loading");
    const [announcement, setAnnouncement] = useState<Announcement>();
    const [error, setError] = useState<string>();
    const [report, setReport] = useState<AnouncementReport>();
    const [page, setPage] = useState<number>(initialPage);
    const [pageSize, setPageSize] = useState<number>(initialPageSize);
    const [loadingItems, setLoadingItems] = useState<boolean>(false);
    const [exportingReport, setExportingReport] = useState<boolean>(false);
    const [exportResult, setExportResult] = useState<ExportResponse>();


    const load = () => {
        setStatus("loading");
        getAnnouncement(context.session!.tenant!.id, match.params.announcementId).then((announcement) => {
            setAnnouncement(announcement);
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setStatus("loaded");
        });
    }

    const loadReport = () => {
        setLoadingItems(true);
        let offset = getOffset(page, pageSize);
        getReport(context.session!.tenant!.id, match.params.announcementId, pageSize, offset).then((report) => {
            setReport(report);
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setLoadingItems(false);
        });
    }
    useEffect(loadReport, [page, pageSize]);

    useEffect(load, [context.session]);

    const onChangedPage = (page: number) => {
        setPage(page);
    };

    const onChangedPageSize = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const onClosedSnackbars = () => {
        setError(undefined);
    };

    const onExportReport = () => {
        setExportingReport(true);
        exportReport(context.session!.tenant!.id, match.params.announcementId).then((report) => {
            window.open(report.key, "_blank")
        }).catch((error) => {
            setError(error.message);
        }).finally(() => {
            setExportingReport(false);
        });
    }

    if ("loading" === status) {
        return (
            <Progress />
        );
    }

    return (
        <Box>
            <Surface title={translate("providers.announcements.announcements_report.title") as string}
                subtitle={translate("providers.announcements.announcements_report.sub_title", { "announcement_name": announcement?.title, "start_date": formatDate(announcement?.start_date, "DD/MM/YYYY"), "end_date": formatDate(announcement?.end_date, "DD/MM/YYYY") }) as string}
                backButton
                icon={<DetailsIcon />}
                className="PaperPagination"
                titleActions={
                    (<Grid container alignItems="center" justify="flex-end" spacing={1}>
                        <Grid item xs="auto">
                            <Fab color="primary" size="small" title={translate("buttons.download") as string} onClick={onExportReport}>
                                <DownloadIcon fontSize="small" />
                            </Fab>
                        </Grid>
                    </Grid>)
                }>

                <Card variant="outlined" style={{ margin: "10px" }}>
                    <CardHeader subheader={translate("providers.announcements.announcements_report.announcements_status") as string} color="secondary">
                    </CardHeader>

                    <CardContent>
                        <Divider />
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <List>
                                    <ListItem>
                                        <ListItemText primary={translate("providers.announcements.announcements_report.providers_accepted") as string} />
                                        <ListItemSecondaryAction>
                                            <Typography variant="h6" color="primary">
                                                <NumberFormat value={report?.total_accepted ?? 0} displayType="text" color="primary" />
                                            </Typography>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={6}>
                                <List>
                                    <ListItem>
                                        <ListItemText primary={translate("providers.announcements.announcements_report.providerst_to_accept") as string} />
                                        <ListItemSecondaryAction>
                                            <Typography variant="h6" color="primary">
                                                <NumberFormat value={report?.pending_accepted ?? 0} displayType="text" color="primary" />
                                            </Typography>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                <Grid item xs={12} >
                    <Box pt={0}>
                        <Pagination variant="embeded"
                            icon={undefined}
                            title={""}
                            page={page}
                            pageSize={pageSize}
                            count={report?.providers?.length ?? 0}
                            total={report?.total ?? 0}
                            onChangedPage={onChangedPage}
                            onChangedPageSize={onChangedPageSize}
                            hideFooterActions={!announcement?.all_providers}
                        >
                            <Gridable
                                items={report?.providers ?? []}
                                loading={loadingItems}
                                empty={translate("providers.announcements.announcements_report.empty_report") as string}
                                columns={[
                                    {
                                        title: translate("providers.announcements.announcements_report.columns.name") as string,
                                        converter: (item) => item.name,
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: <>
                                            <Typography variant="body2" component="h6" align="center">
                                                <strong>{translate("providers.announcements.announcements_report.columns.total_accepted") as string}</strong>
                                            </Typography>
                                        </>,
                                        converter: (item) => item?.users_accepted ?? "0",
                                        justify: "center",
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: <>
                                            <Typography variant="body2" component="h6" align="center">
                                                <strong>{translate("providers.announcements.announcements_report.columns.total_pending") as string}</strong>
                                            </Typography>
                                        </>,
                                        converter: (item) => item?.users_pending ?? "0",
                                        justify: "center",
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: <>
                                            <Typography variant="body2" component="h6" align="center">
                                                <strong>{translate("providers.announcements.announcements_report.columns.total_users") as string}</strong>
                                            </Typography>
                                        </>,
                                        converter: (item) => item?.total_users ?? "0",
                                        justify: "center",
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: translate("providers.announcements.announcements_report.columns.first_date") as string,
                                        converter: (item) => item?.first_date ? <DateFormat date={(item.first_date as any)} format="DD/MM/YYYY" /> : <div>{""}</div>,
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: translate("providers.announcements.announcements_report.columns.last_date") as string,
                                        converter: (item) => item?.last_date ? <DateFormat date={(item.last_date as any)} format="DD/MM/YYYY" /> : <div>{""}</div>,
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    },
                                    {
                                        title: translate("providers.announcements.announcements_report.columns.status") as string,
                                        converter: (item) => item?.status,
                                        xs: true,
                                        sm: true,
                                        md: true,
                                        lg: true,
                                        xl: true
                                    }
                                ]}
                            />
                        </Pagination>
                    </Box>
                </Grid>
            </Surface>
            <ErrorSnackbar message={error} onClose={onClosedSnackbars} />
            <CustomBackdrop open={loadingItems || exportingReport} message={translate("providers.announcements.announcements_report.loading") as string} />
        </Box>

    );
}
